var _a;
import { DateRange, SplitBy, } from '@intentsify/types';
import chunk from 'lodash/chunk';
import { DateTime, Interval } from 'luxon';
/**
 * Shows ranges of weeks as a number.
 * */
var DateRangeToWeekValue = (_a = {},
    _a[DateRange.LastFullWeek] = 1,
    _a[DateRange.LastTwoWeeks] = 2,
    _a[DateRange.LastFourWeeks] = 4,
    _a[DateRange.LastEightWeeks] = 8,
    _a[DateRange.LastTwelveWeeks] = 12,
    _a);
var offsetDate = function (date, offset) {
    if (!offset || !offset.minus) {
        return date;
    }
    return date.minus(offset.minus);
};
/**
 * When users query for full date ranges, we need to take few things into account.
 *
 * 1. If start date is in the future, we just give them the last week of data, that's what they are
 * looking for anyway. We assume the start date is in the future if its after the start of the last week, as we only
 * have data up to that moment in time.
 *
 * 2. If end date is in the future but start date is not in the future we want to return
 * a valid date range but without any unnecessary future dates. So the end date becomes
 * the end of the previous week as we only have the data up to the previous week.
 *
 * 3. If campaign has ended, the full range will include all dates up to the end of the previous week.
 */
var getDatesForFullDateRangeQueries = function (start) {
    var isStartFutureDate = start.toUTC() > DateTime.now().minus({ week: 1 }).startOf('week').toUTC();
    if (isStartFutureDate) {
        return {
            start: DateTime.utc().minus({ week: 1 }).startOf('week'),
            end: DateTime.utc().minus({ week: 1 }).endOf('week'),
        };
    }
    return {
        start: start.toUTC().startOf('week'),
        end: DateTime.now().toUTC().minus({ week: 1 }).endOf('week'),
    };
};
/**
 * Attempt to figure out campaigns start and end date.
 * At the moment we cannot trust the data fully.
 */
export var validateAndGetCampaignStartAndEndDate = function (_a) {
    var startDate = _a.startDate, endDate = _a.endDate;
    // campaign has no dates at all
    if (!endDate && !startDate) {
        throw new Error("Campaign has no startDate or endDate");
    }
    if (endDate && startDate) {
        // has dates, but they are invalid
        if (startDate > endDate) {
            throw new Error("Campaign has startDate after endDate");
        }
        // has valid dates, just return them
        return {
            start: DateTime.fromJSDate(startDate).toUTC(),
            end: DateTime.fromJSDate(endDate).toUTC(),
        };
    }
    // doesn't have an endDate but has startDate; endDate = startDate + 1 year
    if (!endDate && startDate) {
        return {
            start: DateTime.fromJSDate(startDate).toUTC(),
            end: DateTime.fromJSDate(startDate).toUTC().plus({ years: 1 }),
        };
    }
    throw new Error("Campaign has no startDate");
};
/**
 * Limit the date range reach into the past if a campaign doesn't have historical data because it only started recently.
 * E.g.: if user selects last 8 weeks of data but campaign has only started 4 weeks ago,
 * we can safely default to total and disregard the other 4 weeks.
 * */
var getProperDateRange = function (startDate, dateRange) {
    var weeks = DateTime.now().diff(startDate, ['weeks']).weeks;
    var weeksDiff = weeks.toFixed(0);
    return dateRange !== DateRange.Custom &&
        dateRange !== DateRange.Full &&
        Number(weeksDiff) < Number(DateRangeToWeekValue[dateRange])
        ? DateRange.Full
        : dateRange;
};
/**
 * Converts a DateRangeEnum into relative START and END date objects.
 */
export var getDatesFromDateRange = function (dateRange, startDate, endDate) {
    var start = validateAndGetCampaignStartAndEndDate({
        startDate: startDate,
        endDate: endDate,
    }).start;
    var selectedDateRange = getProperDateRange(start, dateRange);
    switch (true) {
        case selectedDateRange === DateRange.Full:
            return getDatesForFullDateRangeQueries(start);
        case selectedDateRange === DateRange.LastFullWeek:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 1 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastTwoWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 2 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastFourWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 4 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastEightWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 8 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastTwelveWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 12 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        default:
            return { start: DateTime.utc().startOf('month'), end: DateTime.utc() };
    }
};
var calculateDateRanges = function (_a) {
    var start = _a.start, end = _a.end, splitBy = _a.splitBy;
    switch (true) {
        case splitBy === SplitBy.Nothing: {
            var startOf = start.startOf('week');
            var endOf = end.endOf('week');
            var interval = Interval.fromDateTimes(startOf, endOf);
            var weeks_1 = interval.splitBy({ weeks: 1 });
            var ranges_1 = [];
            weeks_1.forEach(function (c) {
                var week = c.start.weekNumber;
                var year = weeks_1.length === 1 ? c.end.year : c.start.year;
                var r = ranges_1.find(function (i) { return i.yearNumber === year; });
                if (r === null || r === void 0 ? void 0 : r.weekNumbers) {
                    r.weekNumbers.push(week);
                }
                else {
                    ranges_1.push({
                        yearNumber: year,
                        weekNumbers: [week],
                    });
                }
            });
            return {
                total: {
                    start: startOf.toUTC().toISO(),
                    end: endOf.toUTC().toISO(),
                },
                splits: [
                    {
                        ranges: ranges_1,
                        dateRange: {
                            start: start.startOf('week').toUTC().toISO(),
                            end: end.endOf('week').toUTC().toISO(),
                        },
                    },
                ],
            };
        }
        case splitBy === SplitBy.OneWeek: {
            var startOf_1 = start.startOf('week');
            var endOf = end.endOf('week');
            var interval = Interval.fromDateTimes(startOf_1, endOf);
            var durationInWeek = Math.ceil(interval.length('weeks'));
            return {
                total: {
                    start: startOf_1.toUTC().toISO(),
                    end: endOf.toUTC().toISO(),
                },
                splits: Array(durationInWeek)
                    .fill(undefined)
                    .map(function (_, index) {
                    var split = {
                        ranges: [
                            {
                                yearNumber: startOf_1.plus({ weeks: index }).year,
                                weekNumbers: [startOf_1.plus({ weeks: index }).weekNumber],
                            },
                        ],
                        dateRange: {
                            start: startOf_1.plus({ weeks: index }).toUTC().toISO(),
                            end: startOf_1
                                .plus({ weeks: index })
                                .endOf('week')
                                .toUTC()
                                .toISO(),
                        },
                    };
                    return split;
                }),
            };
        }
        case splitBy === SplitBy.FourWeeks: {
            var startOf = start.startOf('week');
            var endOf = end.endOf('week');
            var interval = Interval.fromDateTimes(startOf, endOf);
            var weeks = interval.splitBy({ weeks: 1 });
            var chunks = chunk(weeks, 4);
            return {
                total: {
                    start: startOf.toUTC().toISO(),
                    end: endOf.toUTC().toISO(),
                },
                splits: chunks.map(function (singleChunk) {
                    var ranges = [];
                    singleChunk.forEach(function (c) {
                        var week = c.start.weekNumber;
                        var year = c.start.year;
                        var r = ranges.find(function (i) { return i.yearNumber === year; });
                        if (r === null || r === void 0 ? void 0 : r.weekNumbers) {
                            r.weekNumbers.push(week);
                        }
                        else {
                            ranges.push({
                                yearNumber: year,
                                weekNumbers: [week],
                            });
                        }
                    });
                    var split = {
                        ranges: ranges,
                        dateRange: {
                            start: singleChunk[0].start.toUTC().toISO(),
                            end: singleChunk[singleChunk.length - 1].start
                                .endOf('week')
                                .toUTC()
                                .toISO(),
                        },
                    };
                    return split;
                }),
            };
        }
        default:
            return {
                splits: [],
                total: {
                    start: start.toISO(),
                    end: end.toISO(),
                },
            };
    }
};
/**
 * Creates a QueryDateRange object from a DateRangeEnum and SplitBy to be used to generate a SQL query.
 *
 * @example
 * If I want to have 8 weeks of data split by 4 weeks and its the begging of 2021 it would give you these splits:
 *
 * # Usage
 * ```ts
 * const dateRange = getDateRange(
 *   DateRange.LastEightWeeks,
 *   SplitBy.FourWeeks,
 *   new Date("2020-12-20T23:00:00.000Z"),
 *   new Date("2021-01-17T22:59:59.999Z")
 * )
 * ```
 *
 * # Result
 * ```ts
 * [
 *   {
 *     ranges: [
 *       {
 *         yearNumber: 2020,
 *         weekNumbers: [
 *           48,
 *           49,
 *           50,
 *           51,
 *         ],
 *       },
 *     ],
 *     dateRange: {
 *       start: "2020-11-22T23:00:00.000Z",
 *       end: "2020-12-20T22:59:59.999Z",
 *     },
 *   },
 *   {
 *     ranges: [
 *       {
 *         yearNumber: 2020,
 *         weekNumbers: [
 *           52,
 *           53,
 *         ],
 *       },
 *       {
 *         yearNumber: 2021,
 *         weekNumbers: [
 *           1,
 *           2,
 *         ],
 *       },
 *     ],
 *     dateRange: {
 *       start: "2020-12-20T23:00:00.000Z",
 *       end: "2021-01-17T22:59:59.999Z",
 *     },
 *   },
 * ]
 * ```
 */
export var getDateRange = function (_a) {
    var range = _a.range, splitBy = _a.splitBy, startDate = _a.startDate, endDate = _a.endDate, offset = _a.offset, customDateRange = _a.customDateRange;
    if (range === DateRange.Custom && typeof customDateRange === 'undefined') {
        throw new Error('When rage is set to "custom", customDateRange needs to be provided');
    }
    var _b = getDatesFromDateRange(range, startDate, endDate), start = _b.start, end = _b.end;
    if (!start) {
        return {
            splits: [],
            total: {
                start: DateTime.utc().toISO(),
                end: DateTime.utc().toISO(),
            },
        };
    }
    return calculateDateRanges({
        start: offsetDate(start, offset),
        end: offsetDate(end, offset),
        splitBy: splitBy,
    });
};
export var getCustomDateRange = function (_a) {
    var splitBy = _a.splitBy, customDateRange = _a.customDateRange;
    return calculateDateRanges({
        start: customDateRange.start,
        end: customDateRange.end,
        splitBy: splitBy,
    });
};
export var getLastWeekAndYearNumbers = function () {
    var _a = DateTime.now().minus({ weeks: 1 }), weekNumber = _a.weekNumber, year = _a.year;
    return {
        weekNumber: weekNumber,
        yearNumber: year,
    };
};
//# sourceMappingURL=dateRange.js.map